/**
 * The day layer (R1 to R5, R10, KTD2): which standards a unit's school days carry, and what a
 * day item may hold. The spread is recomputed on every read; only overrides are stored, so
 * unmoved standards re-spread whenever the unit's days change. Pure.
 */
import { schoolDays } from './calendar';
import { schedule, type ScheduledUnit } from './schedule';
import type { Calendar, IsoDate, ItemMaterial, Plan, SpanOverride } from './types';

/** A standard's run of days, counted in its unit's school days from 0. */
export interface DaySpan {
  standardId: string;
  start: number;
  length: number;
}

export interface StandardSpan extends DaySpan {
  /** The school days the span covers, in order. */
  dates: IsoDate[];
  /** The teacher placed it (R3); otherwise it is on the spread. */
  moved: boolean;
}

export interface UnitSpans {
  unitId: string;
  days: IsoDate[];
  /** Every standard placed in the unit, in plan order; empty when the unit has no school days. */
  spans: StandardSpan[];
}

/** The school days of a unit's weeks, in order. Weeks past the last day hold none. */
export function unitSchoolDays(unit: Pick<ScheduledUnit, 'weekStarts'>, calendar: Calendar): IsoDate[] {
  return unit.weekStarts.flatMap(monday => schoolDays(calendar, monday));
}

/**
 * KTD2: contiguous spans in the given order. With at least as many days as standards, lengths
 * differ by at most one and cover every day; with more standards, they share days in order,
 * one day each, at most ⌈standards / days⌉ a day, and every day still holds one.
 */
export function spread(standardIds: readonly string[], dayCount: number): DaySpan[] {
  const n = standardIds.length;
  const days = Math.max(0, Math.floor(dayCount));
  if (days === 0 || n === 0) return [];
  if (n > days) return standardIds.map((standardId, i) => ({ standardId, start: Math.floor((i * days) / n), length: 1 }));
  const base = Math.floor(days / n);
  const longer = days % n;
  let start = 0;
  return standardIds.map((standardId, i) => {
    const length = base + (i < longer ? 1 : 0);
    const span = { standardId, start, length };
    start += length;
    return span;
  });
}

/** R3: a span of whole days that lies inside a unit of `dayCount` school days. */
export const fitsUnit = ({ start, length }: Pick<DaySpan, 'start' | 'length'>, dayCount: number): boolean =>
  Number.isInteger(start) && Number.isInteger(length) && start >= 0 && length >= 1 && start + length <= dayCount;

/** KTD2: an override that no longer fits after the unit shrinks ends on the unit's last day. */
function clip(o: SpanOverride, dayCount: number): DaySpan | null {
  if (!Number.isInteger(o.start) || !Number.isInteger(o.length) || o.start < 0 || o.length < 1 || dayCount === 0) return null;
  const last = dayCount - 1;
  const start = Math.min(o.start, last);
  return { standardId: o.standardId, start, length: Math.min(o.start + o.length - 1, last) - start + 1 };
}

/**
 * Each unit's standards on its days (R1, R2, KTD2): overridden standards keep their days while
 * they are still placed in the override's unit; the rest spread in plan order over every day.
 */
export function effectiveSpans(plan: Pick<Plan, 'calendar' | 'units' | 'standards' | 'spanOverrides'>): UnitSpans[] {
  const overrides = plan.spanOverrides ?? [];
  return schedule(plan, plan.calendar).map(unit => {
    const days = unitSchoolDays(unit, plan.calendar);
    const placed = plan.standards.filter(s => s.placement.place === 'unit' && s.placement.unitId === unit.id).map(s => s.id);
    const moved = new Map<string, DaySpan>();
    for (const o of overrides) {
      const span = o.unitId === unit.id && placed.includes(o.standardId) && !moved.has(o.standardId) ? clip(o, days.length) : null;
      if (span) moved.set(o.standardId, span);
    }
    const spreadSpans = new Map(spread(placed.filter(id => !moved.has(id)), days.length).map(s => [s.standardId, s]));
    const spans = days.length === 0 ? [] : placed.map((id): StandardSpan => {
      const span = moved.get(id) ?? spreadSpans.get(id)!;
      return { ...span, dates: days.slice(span.start, span.start + span.length), moved: moved.has(id) };
    });
    return { unitId: unit.id, days, spans };
  });
}

/** The standards whose span covers a date, in unit then plan order (R5). */
export function standardsOn(units: readonly UnitSpans[], date: IsoDate): string[] {
  return units.flatMap(u => u.spans.filter(s => s.dates.includes(date)).map(s => s.standardId));
}

/**
 * R5: the standards a day item may be tagged with: the course's, in course order, less any the
 * teacher placed `not_in_course`. The tag picker and the item routes both read this.
 */
export function courseStandardIds(record: { standards: readonly { id: string }[]; plan: Pick<Plan, 'standards'> }): string[] {
  const excluded = new Set(record.plan.standards.filter(s => s.placement.place === 'not_in_course').map(s => s.id));
  return record.standards.map(s => s.id).filter(id => !excluded.has(id));
}

export const MAX_ITEM_TITLE = 120;
/** A hand-off link carries every tag, so the count stays small (plan Assumptions). */
export const MAX_ITEM_TAGS = 20;

/** What a teacher writes on a day (R4). */
export interface DayItemFields {
  title: string;
  link: string | null;
  standardIds: string[];
}

export type DayItemRefusal = 'title_invalid' | 'link_invalid' | 'tags_invalid' | 'too_many_tags' | 'tag_not_in_course';
export type DayItemParse = { ok: true; item: DayItemFields } | { ok: false; reason: DayItemRefusal };

/** An http(s) link, or null; anything else, `javascript:` included, is refused. */
function parseLink(value: unknown): string | null | undefined {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value !== 'string') return undefined;
  try {
    const url = new URL(value.trim());
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : undefined;
  } catch {
    return undefined;
  }
}

/**
 * A day item from untrusted input (R4): a one-line title of up to 120 characters, an optional
 * http(s) link, and up to 20 distinct tags from the plan's course.
 */
export function parseDayItem(value: unknown, courseIds: readonly string[]): DayItemParse {
  const v = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  const title = typeof v.title === 'string' ? v.title.trim() : '';
  if (!title || title.length > MAX_ITEM_TITLE || /[\r\n\u2028\u2029]/.test(title)) return { ok: false, reason: 'title_invalid' };
  const link = parseLink(v.link);
  if (link === undefined) return { ok: false, reason: 'link_invalid' };
  const tags = v.standardIds ?? [];
  if (!Array.isArray(tags) || !tags.every(t => typeof t === 'string')) return { ok: false, reason: 'tags_invalid' };
  const standardIds = [...new Set(tags as string[])];
  if (standardIds.length > MAX_ITEM_TAGS) return { ok: false, reason: 'too_many_tags' };
  const course = new Set(courseIds);
  if (!standardIds.every(id => course.has(id))) return { ok: false, reason: 'tag_not_in_course' };
  return { ok: true, item: { title, link, standardIds } };
}

/** R10: "Brush up" and "Check my lesson" show only on a tagged item. */
export const itemHasActions = (item: Pick<DayItemFields, 'standardIds'>): boolean => item.standardIds.length > 0;

/** R1: a day item holds at most this many Collab materials. */
export const MAX_ITEM_MATERIALS = 10;
/** A Collab id: a lowercase UUID, so it can never shape a path on its way to Collab (KTD1, KTD2). */
export const MATERIAL_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** What a teacher writes on a day with Collab attachments on (R1, R2, KTD2). */
export interface AttachedItemFields extends DayItemFields {
  /** The materials in order, or null when the write leaves them as stored. */
  materialIds: string[] | null;
}

export type AttachedItemRefusal = DayItemRefusal | 'link_not_accepted' | 'materials_invalid' | 'too_many_materials';
export type AttachedItemParse = { ok: true; item: AttachedItemFields } | { ok: false; reason: AttachedItemRefusal };

/**
 * A day item from untrusted input with Collab attachments on (R1, R2, KTD2): parseDayItem's
 * title and tags, up to ten distinct material ids, and no new link. `stored` is the item being
 * edited, null for a new one. A stored link stays when the write leaves it out or repeats it,
 * and goes on null or empty; any other link is refused. Absent `materialIds` keeps the stored
 * attachments; a list replaces them.
 */
export function parseAttachedItem(value: unknown, courseIds: readonly string[], stored: { link: string | null } | null): AttachedItemParse {
  const v = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  const base = parseDayItem({ ...v, link: null }, courseIds);
  if (!base.ok) return base;

  let link: string | null;
  if (v.link === undefined) link = stored?.link ?? null;
  else if (v.link === null || v.link === '') link = null;
  else if (stored?.link && parseLink(v.link) === stored.link) link = stored.link;
  else return { ok: false, reason: 'link_not_accepted' };

  let materialIds: string[] | null = null;
  if (v.materialIds !== undefined) {
    if (!Array.isArray(v.materialIds) || !v.materialIds.every(id => typeof id === 'string' && MATERIAL_ID.test(id))) return { ok: false, reason: 'materials_invalid' };
    materialIds = [...new Set(v.materialIds as string[])];
    if (materialIds.length > MAX_ITEM_MATERIALS) return { ok: false, reason: 'too_many_materials' };
  }
  return { ok: true, item: { ...base.item, link, materialIds } };
}

/** KTD2: the requested ids not already attached, the only ones Collab is asked about. */
export const newMaterialIds = (stored: readonly ItemMaterial[], requested: readonly string[]): string[] => {
  const have = new Set(stored.map(m => m.materialId));
  return requested.filter(id => !have.has(id));
};

/** KTD2: the snapshots in the requested order; stored ones keep their title, removed ones go. */
export const orderMaterials = (requested: readonly string[], snapshots: readonly ItemMaterial[]): ItemMaterial[] => {
  const byId = new Map(snapshots.map(m => [m.materialId, m]));
  return requested.flatMap(id => byId.get(id) ?? []);
};
